import { Delete as DeleteIcon } from '@mui/icons-material'
import { Box, Button, FormControl, IconButton, Stack } from '@mui/material'
import { ArrayPath, FieldArrayWithId, FieldValues, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BoxForm } from 'components/Form'
import { FieldArrayInputColumn, FieldArrayInputProps } from './types'

export const FieldArrayInput = <T extends FieldValues>({
  addNextDisabled,
  control,
  defaults,
  inputs,
  name,
}: FieldArrayInputProps<T>): React.JSX.Element => {
  const { t } = useTranslation('features/user')
  const { append, fields, remove } = useFieldArray<T>({
    control,
    name,
  })

  const createDefaults = (): FieldArrayWithId<T, ArrayPath<T>, 'id'> => {
    return Object.assign({ id: '' }, defaults)
  }

  const headerColumns = (): React.JSX.Element[] =>
    inputs.map((column: FieldArrayInputColumn, columnIndex: number) => (
      <FormControl key={`${name}-header-${columnIndex}`}>{column.label}</FormControl>
    ))

  const bodyColumns = (index: number): React.JSX.Element[] =>
    inputs.map((column: FieldArrayInputColumn): React.JSX.Element => column.cell(index))

  return (
    <BoxForm>
      <Stack
        direction='row'
        sx={{ gap: 2 }}
      >
        {headerColumns()}
      </Stack>

      {fields.map((field: FieldArrayWithId<T, ArrayPath<T>, 'id'>, index: number) => (
        <Stack
          direction='row'
          key={field.id}
          sx={{ gap: 2 }}
        >
          {bodyColumns(index)}

          <Box sx={{ pt: 1 }}>
            <IconButton onClick={(): void => remove(index)}>
              <DeleteIcon data-cy='delete-icon' />
            </IconButton>
          </Box>
        </Stack>
      ))}

      <Box sx={{ pt: 1 }}>
        <Button
          data-cy='add-next'
          disabled={addNextDisabled}
          onClick={() => append(createDefaults())}
          type='button'
        >
          {t('form.publisher.addNext')}
        </Button>
      </Box>
    </BoxForm>
  )
}
